import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Bell } from 'lucide-react';
import { Link } from 'react-router-dom';
import { fmtETDateTime } from '../utils/tz';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NotificationBell = () => {
  const [data, setData] = useState({ notifications: [], unread_count: 0 });
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const fetchData = async () => {
    try {
      const { data } = await axios.get(`${API}/notifications`, { withCredentials: true });
      setData(data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, 60000);
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => { clearInterval(id); document.removeEventListener('mousedown', onClick); };
  }, []);

  const markRead = async (id) => {
    try { await axios.put(`${API}/notifications/${id}/read`, {}, { withCredentials: true }); fetchData(); } catch {}
  };
  const markAll = async () => {
    try { await axios.put(`${API}/notifications/read-all`, {}, { withCredentials: true }); fetchData(); } catch {}
  };

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="notification-bell"
        onClick={() => { setOpen(!open); fetchData(); }}
        className="relative p-2 rounded-full hover:bg-[#262626] transition-colors"
      >
        <Bell className="w-5 h-5 text-white" />
        {data.unread_count > 0 && (
          <span data-testid="notification-unread-count" className="absolute -top-1 -right-1 bg-[#D4AF37] text-[#0A0A0A] text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
            {data.unread_count > 9 ? '9+' : data.unread_count}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="notification-panel" className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto bg-[#141414] border border-[#262626] rounded-md shadow-xl z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#262626]">
            <p className="text-white font-bold text-sm">Notifications</p>
            {data.unread_count > 0 && (
              <button data-testid="mark-all-read" onClick={markAll} className="text-[#D4AF37] text-xs hover:underline">Mark all read</button>
            )}
          </div>
          {data.notifications.length === 0 ? (
            <p className="text-[#A3A3A3] text-sm text-center py-8">No notifications</p>
          ) : data.notifications.map((n) => (
            <Link
              key={n.id}
              to={n.link || '#'}
              data-testid={`notif-${n.id}`}
              onClick={() => { markRead(n.id); setOpen(false); }}
              className={`block px-4 py-3 border-b border-[#262626] last:border-0 hover:bg-[#0A0A0A] transition-colors ${!n.read ? 'bg-[#D4AF37]/5' : ''}`}
            >
              <div className="flex items-start gap-2">
                {!n.read && <span className="w-2 h-2 rounded-full bg-[#D4AF37] mt-1.5" />}
                <div className="flex-1">
                  <p className="text-white text-sm font-medium">{n.title}</p>
                  <p className="text-[#A3A3A3] text-xs mt-0.5">{n.message}</p>
                  <p className="text-[#525252] text-[10px] mt-1">{fmtETDateTime(n.created_at)}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
